<template>
  <div
    class="multisteps-form__panel border-radius-xl bg-white"
    data-animation="FadeIn"
  >
    <h5 class="font-weight-bolder">{{ $t('user.Password') }}</h5>
    <p class="mb-0 text-sm">{{ $t('user.Mandatory_informations') }} *</p>
    <div class="multisteps-form__content">
      <div class="row">
        <div class="col-6">
          <div class="row mt-3">
            <div class="col-12">
              <div class="input-group input-group-static null">
                <label class="">{{ $t('user.New_password') }} *</label>
                <input :type="show.new ? 'text' : 'password'" class="password-input" v-model.trim="password" id="password" isrequired="true" @input="validateAll">
                <span class="material-symbols-outlined toggle-icon" @click="toggle('new')">{{ !show.new ? 'visibility' : 'visibility_off' }}</span>              
                <span v-if="password && !allValidationsOk" class="text-sm text-danger">{{ $t('user.New_password_value_is_invalid') }}</span>                  
              </div>
            </div>
          </div>
          <div class="row mt-6">
            <div class="col-12">
              <div class="input-group input-group-static null">
                <label class="">{{ $t('user.Repeat_new_password') }} *</label>
                <input :type="show.confirm ? 'text' : 'password'" class="password-input" v-model.trim="confirmPassword" id="confirmPassword" isrequired="true" @input="validateAll">
                <span class="material-symbols-outlined toggle-icon" @click="toggle('confirm')">{{ !show.confirm ? 'visibility' : 'visibility_off' }}</span>              
                <span v-if="confirmPassword && !passwordsMatch" class="text-sm text-danger">{{ $t('user.Passwords_do_not_match') }}</span>                  
              </div>
            </div>
          </div>            
        </div>
        <div class="col-6">
            <ul class="checklist">
              <li :class="{ok: validations.lengthOk}">
                <Icon :ok="validations.lengthOk" /> {{ $t('user.Longer_than_7_characters') }}
              </li>
              <li :class="{ok: validations.hasUpper}">
                <Icon :ok="validations.hasUpper" /> {{ $t('user.Contains_a_capital_letter') }}
              </li>
              <li :class="{ok: validations.hasLower}">
                <Icon :ok="validations.hasLower" /> {{ $t('user.Contains_a_lowercase_letter') }}
              </li>
              <li :class="{ok: validations.hasDigit}">
                <Icon :ok="validations.hasDigit" /> {{ $t('user.Contains_a_number') }}
              </li>
              <li :class="{ok: validations.hasSpecial}">
                <Icon :ok="validations.hasSpecial" /> {{ $t('user.Contains_a_special character') }}
              </li>
          </ul>
        </div>
      </div>      
      <div class="button-row d-flex mt-4">
        <button class="btn mb-0 bg-gradient-light btn-sm null null js-btn-prev" type="button" @click="this.$parent.prevStep">{{ $t('common.Prev') }}</button>
        <button class="btn mb-0 btn-sm bg-gradient-success null null ms-auto js-btn-next" type="button" :disabled="!canSubmit" @click="validateAndNextStep">{{ $t('common.Next') }}</button>                
      </div>
    </div>
  </div>
</template>

<script>
  import { computed, reactive, watch, ref  } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { mapMutations } from "vuex";
  import { h } from 'vue';
  
  const Icon = {
    name: 'Icon',
    props: { ok: { type: Boolean, default: false } },
    render() {
      if (this.ok) {
        return h('svg',
          {
            xmlns: 'http://www.w3.org/2000/svg',
            width: '16',
            height: '16',
            viewBox: '0 0 24 24',
            'aria-hidden': 'true'
          },
          [
            h('path', {
              fill: '#1aa34a',
              d: 'M9 16.2l-3.5-3.5-1.4 1.4L9 19 20 8l-1.4-1.4z'
            })
          ]
        );
      } else {
        return h('svg',
          {
            xmlns: 'http://www.w3.org/2000/svg',
            width: '16',
            height: '16',
            viewBox: '0 0 24 24',
            'aria-hidden': 'true'
          },
          [
            h('path', {
              fill: '#d33',
              d: 'M12 10.6l4.9-4.9 1.4 1.4L13.4 12l4.9 4.9-1.4 1.4L12 13.4l-4.9 4.9-1.4-1.4L10.6 12 5.7 7.1 7.1 5.7 12 10.6z'
            })
          ]
        );
      }
    }
  };

  export default {
    name: "PasswordSection",
    components: { Icon },
    props: {
      formData: Object,
      nextStep: Function
    },    
    setup(props, { emit }) {
      const { t } = useI18n()

      // password fields
      const password = ref(props.formData.password || '')
      const confirmPassword = ref(props.formData.confirmPassword || '')

      // show toggles for visibility
      const show = reactive({ new: false, confirm: false })

      // checklist validations
      const validations = reactive({
        lengthOk: false,
        hasUpper: false,
        hasLower: false,
        hasDigit: false,
        hasSpecial: false
      })

      // run the checklist validation
      function validateAll() {
        const pwd = password.value || ''
        validations.lengthOk = pwd.length >= 8
        validations.hasUpper = /[A-ZÀ-ÖØ-Þ]/.test(pwd)
        validations.hasLower = /[a-zà-öø-ÿ]/.test(pwd)
        validations.hasDigit = /[0-9]/.test(pwd)
        validations.hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(pwd)
      }

      // computed helpers
      const allValidationsOk = computed(() =>
        validations.lengthOk &&
        validations.hasUpper &&
        validations.hasLower &&
        validations.hasDigit &&
        validations.hasSpecial
      )

      const passwordsMatch = computed(() => password.value && confirmPassword.value && password.value === confirmPassword.value)

      const canSubmit = computed(() => allValidationsOk.value && passwordsMatch.value)

      // emit updates to parent
      function emitFormData() {
        emit('update:formData', { ...props.formData, password: password.value, confirmPassword: confirmPassword.value })
      }

      // input handlers
      function onPasswordInput() {
        validateAll()
        emitFormData()
      }
      function onConfirmInput() {
        emitFormData()
      }

      // toggle visibility
      function toggle(which) {
        if (which in show) show[which] = !show[which]
      }

      function validateAndNextStep() {
        validateAll()
        if (allValidationsOk.value && passwordsMatch.value) {
          emitFormData()
          props.nextStep()
        } else {
          // optionally focus the first invalid field
          if (!allValidationsOk.value) {
            // focus password field
            const el = document.getElementById('password')
            if (el) el.focus()
          } else if (!passwordsMatch.value) {
            const el = document.getElementById('confirmPassword')
            if (el) el.focus()
          }
        }
      }

      // watch parent changes
      watch(() => props.formData.password, (v) => {
        if (v !== undefined && v !== password.value) {
          password.value = v || ''
          validateAll()
        }
      })

      // initial validation
      validateAll()

      return {
        t,
        password,
        confirmPassword,
        show,
        validateAll,
        validations,
        allValidationsOk,
        passwordsMatch,
        onPasswordInput,
        onConfirmInput,
        toggle,
        canSubmit,
        validateAndNextStep
      }
    },
    methods: {
      ...mapMutations(["toggleEveryDisplay", "toggleHideConfig"]),
    }    
};
</script>

<style scoped>
.password-input-wrapper {
  position: relative;
  width: 100%;
}

.password-input {
  width: 100%;
  padding-right: 2.5rem; /* Reserve space for the icon */
  border: none;
  border-bottom: 1px solid #ccc;
  outline: none;
}

.password-input:focus {
  border-bottom-color: green; /* or your primary color */
}

.toggle-icon {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  cursor: pointer;
}
</style>


